import { isServer } from '@toss/utils';
import { type SupportedLocale } from '@zep-us/i18n';

import { type PaymentSource } from '@/shared/hooks/usePaymentFlow';

/**
 * 결제 플로우 URL 상수
 * TossPayments successUrl, failUrl 등에서도 사용
 */
export const PAYMENT_URLS = {
  // 메인 플로우 페이지들
  PRICING: '/pricing',
  METHODS: '/checkout/method',
  CHECKOUT: '/checkout',
  EXTEND: '/checkout/extend',
  UPGRADE: '/checkout/upgrade',
  PAYMENT: '/checkout/payment',
  PROCESSING: '/checkout/processing',
  COMPLETE: '/checkout/complete',

  getPaymentUrl: (preOrderId: string, paymentSource?: PaymentSource) => {
    const baseUrl = `/checkout/payment?preOrderId=${preOrderId}`;
    return paymentSource ? `${baseUrl}&source=${paymentSource}` : baseUrl;
  },

  /** 비동기 결제(Antom 등) 진행 화면. 주문 결과가 PROCESSING 일 때 이리로 보낸다. */
  getAsyncProcessingUrl: (orderId: string) =>
    `/checkout/processing?flow=async&orderId=${encodeURIComponent(orderId)}`,
} as const;

/**
 * TossPayments 콜백 API URL 생성.
 *
 * sync 플로우 (KRW 카드 / PayPal): Toss 가 `successUrl` 로 리다이렉트 → processing 페이지에서
 *   `useHandlePaymentSuccessSuspense` 가 서버 confirm 호출 후 complete 로 이동.
 *
 * async 플로우 (Antom 비동기): Toss 가 `pendingUrl` 로 리다이렉트 → processing 페이지에서
 *   `useFetchPaymentStatus` 가 폴링 → APPROVED 시 complete 로 이동.
 *
 * 둘 다 같은 `/checkout/processing` 페이지를 재사용하며, `flow` 쿼리 파라미터로 분기합니다.
 */
export const getTossPaymentsCallbackUrls = (
  preOrderId: string,
  locale: SupportedLocale,
  paymentSource?: PaymentSource,
) => {
  const baseUrl = isServer() ? '' : window.location.origin;
  const sourceQuery = paymentSource ? `&source=${paymentSource}` : '';
  const processingBase = `${baseUrl}/${locale}${PAYMENT_URLS.PROCESSING}?preOrderId=${preOrderId}${sourceQuery}`;
  return {
    successUrl: `${processingBase}&flow=sync`,
    pendingUrl: `${processingBase}&flow=async`,
    failUrl: `${baseUrl}/${locale}${PAYMENT_URLS.getPaymentUrl(
      preOrderId,
      paymentSource,
    )}`,
  };
};
